import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Factory, ShieldCheck, LogOut, Package, FileText, Blinds, Frame, Grid3x3, Ruler, Layers, Image as ImageIcon, PanelLeftClose, PanelLeftOpen, SlidersHorizontal, Paintbrush } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSidebar } from "@/context/SidebarContext";

export const AppHeader = () => {
  const loc = useLocation();
  const { user, isAdmin, canAccess, logout } = useAuth();
  const { collapsed, toggle } = useSidebar();

  const navCls = (active) =>
    `w-full ${collapsed ? "justify-center px-0" : "px-4"} py-2.5 rounded-md text-sm font-medium transition-colors flex items-center gap-3 ${
      active ? "bg-amber-50 text-amber-700" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
    }`;

  const isActive = (p) => loc.pathname.startsWith(p);

  const NavItem = ({ to, testid, icon: Icon, label, active }) => (
    <Link to={to} data-testid={testid} className={navCls(active)} title={collapsed ? label : undefined}>
      <Icon className="h-4 w-4 shrink-0" />
      {!collapsed && <span>{label}</span>}
    </Link>
  );

  return (
    <aside
      className={`fixed top-0 left-0 h-screen ${collapsed ? "w-16" : "w-60"} bg-white border-r-4 border-amber-500 shadow-sm z-40 flex flex-col transition-[width] duration-200`}
      data-testid="app-header"
    >
      <div className={`flex items-center h-16 border-b border-slate-100 shrink-0 ${collapsed ? "justify-center px-0" : "gap-2 px-3"}`}>
        <Link to="/comenzi" className="flex items-center gap-3 group min-w-0" data-testid="logo-link" title={collapsed ? "AmelProd" : undefined}>
          <div className="h-10 w-10 rounded-lg bg-amber-500 flex items-center justify-center shadow-md group-hover:scale-105 transition-transform shrink-0">
            <Factory className="h-6 w-6 text-white" />
          </div>
          {!collapsed && (
            <div className="leading-tight min-w-0">
              <div className="text-slate-900 font-bold text-lg tracking-tight truncate">AmelProd</div>
              <div className="text-slate-400 text-[11px] uppercase tracking-widest">Documente productie</div>
            </div>
          )}
        </Link>
        {!collapsed && (
          <button
            onClick={toggle}
            data-testid="sidebar-toggle"
            className="ml-auto p-2 rounded-md text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-colors shrink-0"
            title="Restrange meniul"
          >
            <PanelLeftClose className="h-4 w-4" />
          </button>
        )}
      </div>

      {collapsed && (
        <button
          onClick={toggle}
          data-testid="sidebar-toggle"
          className="mx-auto mt-2 p-2 rounded-md text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-colors"
          title="Extinde meniul"
        >
          <PanelLeftOpen className="h-4 w-4" />
        </button>
      )}

      <nav className="flex-1 flex flex-col gap-1 p-3 overflow-y-auto overflow-x-hidden">
        <NavItem to="/comenzi" testid="nav-comenzi" icon={Package} label="Comenzi" active={isActive("/comenzi") || isActive("/comanda")} />
        {canAccess("documente") && <NavItem to="/documente" testid="nav-documente" icon={FileText} label="Documente" active={isActive("/documente")} />}
        {canAccess("obloane") && <NavItem to="/obloane" testid="nav-obloane" icon={Blinds} label="Obloane" active={isActive("/obloane")} />}
        {canAccess("baghete") && <NavItem to="/baghete" testid="nav-baghete" icon={Frame} label="Baghete" active={isActive("/baghete")} />}
        {canAccess("sprose") && <NavItem to="/sprose" testid="nav-sprose" icon={Grid3x3} label="Sprose" active={isActive("/sprose")} />}
        {canAccess("perimetru") && <NavItem to="/perimetru" testid="nav-perimetru" icon={Ruler} label="Perimetru" active={isActive("/perimetru")} />}
        {canAccess("prelucrate") && <NavItem to="/prelucrate" testid="nav-prelucrate" icon={Layers} label="Prelucrate" active={isActive("/prelucrate")} />}
        {canAccess("imagini") && <NavItem to="/imagini" testid="nav-imagini" icon={ImageIcon} label="Imagini" active={isActive("/imagini")} />}
        {canAccess("obloane_config") && <NavItem to="/configurare-obloane" testid="nav-obloane-config" icon={SlidersHorizontal} label="Config. Obloane" active={isActive("/configurare-obloane")} />}
        {canAccess("perimetru_obloane") && <NavItem to="/vopsire-obloane" testid="nav-perimetru-obloane" icon={Paintbrush} label="Vopsire Obloane" active={isActive("/vopsire-obloane")} />}
        {isAdmin && <NavItem to="/drepturi" testid="nav-drepturi" icon={ShieldCheck} label="Drepturi" active={isActive("/drepturi")} />}
      </nav>

      {user && (
        <div className={`flex items-center border-t border-slate-100 shrink-0 ${collapsed ? "justify-center p-3" : "gap-3 p-4"}`}>
          {!collapsed && (
            <div className="text-left leading-tight flex-1 min-w-0" data-testid="current-user">
              <div className="text-slate-900 text-sm font-medium truncate">{user.nume || user.email}</div>
              <div className="text-slate-400 text-[11px] uppercase tracking-wide">{user.rol === "admin" ? "Admin" : "Operator"}</div>
            </div>
          )}
          <button
            onClick={logout}
            data-testid="logout-button"
            className="p-2 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors shrink-0"
            title="Deconectare"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      )}
    </aside>
  );
};
